import type { TFunction } from 'i18next';

import { CareerErrorCode } from './careerErrors';
import type { ExerciseFieldKind } from './transform';

/**
 * Коды ошибок сдачи анкеты урока (ТЗ 0039) → текст из локали.
 *
 * Что реально приходит в `submitCareerLessonExercise` (бэкенд, `tmsis/career`):
 *   - `services/engagement.py` (`member_and_unlocked_lesson`) и
 *     `services/membership.py` (`complete_lesson`): `not_a_member`,
 *     `not_found`, `lesson_not_published`, `lesson_locked`, `artifact_required`
 *     — все с полем `lessonId`;
 *   - `services/exercise_submit.py`: `not_found` с полем `lessonId` (у урока нет
 *     анкеты) или `answers` (поле анкеты неизвестно — анкету поменяли, пока
 *     студент её заполнял);
 *   - `schema/mutations.py`: `not_found` с полем `student`;
 *   - `services/exercise_values.py`: `required_missing` и `invalid_value` с
 *     полем `field:<id>`.
 *
 * 🔴 Серверный `message` не используется НИКОГДА — он по-русски. Неизвестный
 * код даёт общий текст формы, а не сообщение сервера.
 *
 * `invalid_value` у сервера один на все причины, а причину называл только
 * русский `message`. Её восстанавливаем по ТИПУ поля: у e-mail это адрес, у
 * ссылки — `http(s)://`, у оценки — диапазон 1–5.
 */
export const ExerciseErrorCode = {
  REQUIRED_MISSING: 'required_missing',
  INVALID_VALUE: 'invalid_value',
} as const;

const INVALID_VALUE_TEXT: Partial<Record<ExerciseFieldKind, string>> = {
  email: 'careerCenter.exercise.errors.email',
  url: 'careerCenter.exercise.errors.url',
  date: 'careerCenter.exercise.errors.date',
  month_year: 'careerCenter.exercise.errors.monthYear',
  rating: 'careerCenter.exercise.errors.rating',
  single_choice: 'careerCenter.exercise.errors.choice',
  multi_choice: 'careerCenter.exercise.errors.choice',
  skills: 'careerCenter.exercise.errors.skills',
  repeat_group: 'careerCenter.exercise.errors.repeatGroup',
};

export const getExerciseErrorText = (
  t: TFunction,
  code?: string | null,
  field?: string | null,
  fieldKind?: ExerciseFieldKind | null,
): string => {
  switch (code) {
    case ExerciseErrorCode.REQUIRED_MISSING:
      return t('careerCenter.exercise.required');
    case ExerciseErrorCode.INVALID_VALUE:
      return t(
        (fieldKind && INVALID_VALUE_TEXT[fieldKind]) || 'careerCenter.exercise.errors.invalidValue',
      );
    case CareerErrorCode.NOT_FOUND:
      return field === 'answers'
        ? t('careerCenter.exercise.errors.outdated')
        : t('careerCenter.errors.notFound');
    case CareerErrorCode.NOT_A_MEMBER:
      return t('careerCenter.errors.notAMember');
    case CareerErrorCode.LESSON_NOT_PUBLISHED:
      return t('careerCenter.errors.lessonNotPublished');
    case CareerErrorCode.LESSON_LOCKED:
      return t('careerCenter.errors.lessonLocked');
    case CareerErrorCode.ARTIFACT_REQUIRED:
      return t('careerCenter.errors.artifactRequired');
    default:
      return t('careerCenter.exercise.submitFailed');
  }
};
